import * as Sentry from "@sentry/react";
import type { SignInOutput } from "aws-amplify/auth";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

export interface User {
  email: string;
  name: string;
  emailVerified?: boolean;
  userId?: string;
}

export interface AuthResult {
  success: boolean;
  error?: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Auth methods
  login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResult>;
  signup: (email: string, password: string, name: string) => Promise<SignupResult>;
  logout: () => Promise<void>;
  // Email verification
  confirmEmail: (email: string, code: string) => Promise<AuthResult>;
  resendVerificationCode: (email: string) => Promise<AuthResult>;
  // Password reset
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<AuthResult>;
  // MFA
  setupMfa: () => Promise<MfaSetupResult>;
  verifyMfaSetup: (code: string) => Promise<AuthResult>;
  enableMfa: () => Promise<AuthResult>;
  disableMfa: () => Promise<AuthResult>;
  confirmMfaCode: (code: string) => Promise<AuthResult>;
  // Token access for API calls
  getAccessToken: () => Promise<string | null>;
}

export interface LoginResult {
  success: boolean;
  needsMfaCode?: boolean;
  needsNewPassword?: boolean;
  error?: string;
}

export interface SignupResult {
  success: boolean;
  needsConfirmation?: boolean;
  error?: string;
}

export interface MfaSetupResult {
  success: boolean;
  qrCodeUri?: string;
  secretKey?: string;
  error?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_STORAGE_KEY = "airtightcloud_auth";
const TEST_MODE_KEY = "airtightcloud_test_mode";
const REMEMBER_ME_KEY = "airtightcloud_remember_me";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingSignIn, setPendingSignIn] = useState<SignInOutput | null>(null);

  const checkCurrentUser = useCallback(async () => {
    const { configureAmplify } = await import("../config/amplify");
    configureAmplify();

    // Test mode: skip Cognito calls and use localStorage-only auth (DEV only)
    if (import.meta.env.DEV) {
      const isTestMode = localStorage.getItem(TEST_MODE_KEY) === "true";
      if (isTestMode) {
        const storedUser = localStorage.getItem(AUTH_STORAGE_KEY);
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch {
            setUser(null);
          }
        }
        setIsLoading(false);
        return;
      }
    }

    try {
      const { getCurrentUser, fetchAuthSession } = await import("aws-amplify/auth");
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      // Get user attributes from the ID token
      const idToken = session.tokens?.idToken;
      const email = (idToken?.payload?.email as string) || currentUser.signInDetails?.loginId || "";
      const name = (idToken?.payload?.name as string) || email.split("@")[0];
      const emailVerified = (idToken?.payload?.email_verified as boolean) || false;
      const userId = (idToken?.payload?.sub as string) || currentUser.userId || "";

      const userData: User = { email, name, emailVerified, userId };
      setUser(userData);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
      Sentry.setUser({ id: userId, email });
    } catch {
      // No current user session
      setUser(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    checkCurrentUser();
  }, [checkCurrentUser]);

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean = true
  ): Promise<LoginResult> => {
    try {
      // Store remember me preference for session restoration
      localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? "true" : "false");

      const { signIn } = await import("aws-amplify/auth");
      const result = await signIn({ username: email, password });

      if (result.nextStep.signInStep === "CONFIRM_SIGN_UP") {
        return { success: false, error: "Please verify your email first" };
      }

      if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        setPendingSignIn(result);
        return { success: false, needsMfaCode: true };
      }

      if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        return { success: false, needsNewPassword: true };
      }

      if (result.isSignedIn) {
        await checkCurrentUser();
        return { success: true };
      }

      return { success: false, error: "Sign in failed" };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sign in failed";
      return { success: false, error: message };
    }
  };

  const signup = async (email: string, password: string, name: string): Promise<SignupResult> => {
    try {
      const { signUp } = await import("aws-amplify/auth");
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
          },
        },
      });

      if (result.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        return { success: true, needsConfirmation: true };
      }

      if (result.isSignUpComplete) {
        // Auto sign-in if no confirmation needed (unlikely with email verification)
        await login(email, password);
        return { success: true };
      }

      return { success: false, error: "Sign up failed" };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sign up failed";
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      const { signOut } = await import("aws-amplify/auth");
      await signOut();
    } finally {
      setUser(null);
      setPendingSignIn(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      Sentry.setUser(null);
    }
  };

  const confirmEmail = async (email: string, code: string): Promise<AuthResult> => {
    try {
      const { confirmSignUp } = await import("aws-amplify/auth");
      await confirmSignUp({ username: email, confirmationCode: code });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Email confirmation failed";
      return { success: false, error: message };
    }
  };

  const resendVerificationCode = async (email: string): Promise<AuthResult> => {
    try {
      const { resendSignUpCode } = await import("aws-amplify/auth");
      await resendSignUpCode({ username: email });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to resend verification code";
      return { success: false, error: message };
    }
  };

  const requestPasswordReset = async (email: string): Promise<AuthResult> => {
    try {
      const { resetPassword } = await import("aws-amplify/auth");
      await resetPassword({ username: email });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to request password reset";
      return { success: false, error: message };
    }
  };

  const confirmPasswordReset = async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<AuthResult> => {
    try {
      const { confirmResetPassword } = await import("aws-amplify/auth");
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to reset password";
      return { success: false, error: message };
    }
  };

  const setupMfa = async (): Promise<MfaSetupResult> => {
    try {
      const { setUpTOTP } = await import("aws-amplify/auth");
      const result = await setUpTOTP();
      const qrCodeUri = result.getSetupUri("AirTight Cloud").toString();
      return {
        success: true,
        qrCodeUri,
        secretKey: result.sharedSecret,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "MFA setup failed";
      return { success: false, error: message };
    }
  };

  const verifyMfaSetup = async (code: string): Promise<AuthResult> => {
    try {
      const { verifyTOTPSetup } = await import("aws-amplify/auth");
      await verifyTOTPSetup({ code });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "MFA verification failed";
      return { success: false, error: message };
    }
  };

  const enableMfa = async (): Promise<AuthResult> => {
    try {
      const { updateMFAPreference } = await import("aws-amplify/auth");
      await updateMFAPreference({ totp: "PREFERRED" });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to enable MFA";
      return { success: false, error: message };
    }
  };

  const disableMfa = async (): Promise<AuthResult> => {
    try {
      const { updateMFAPreference } = await import("aws-amplify/auth");
      await updateMFAPreference({ totp: "DISABLED" });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to disable MFA";
      return { success: false, error: message };
    }
  };

  const confirmMfaCode = async (code: string): Promise<AuthResult> => {
    try {
      if (!pendingSignIn) {
        return { success: false, error: "No pending sign-in session" };
      }

      const { confirmSignIn } = await import("aws-amplify/auth");
      const result = await confirmSignIn({ challengeResponse: code });

      if (result.isSignedIn) {
        setPendingSignIn(null);
        await checkCurrentUser();
        return { success: true };
      }

      return { success: false, error: "MFA verification failed" };
    } catch (error) {
      const message = error instanceof Error ? error.message : "MFA verification failed";
      return { success: false, error: message };
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    // Test mode: return a mock token (DEV only)
    if (import.meta.env.DEV) {
      const isTestMode = localStorage.getItem(TEST_MODE_KEY) === "true";
      if (isTestMode) {
        return "mock-test-access-token";
      }
    }

    try {
      const { fetchAuthSession } = await import("aws-amplify/auth");
      const session = await fetchAuthSession();
      // Use the ID token for API authentication: Cognito ID tokens carry the `aud`
      // claim set to the user pool client ID, which the backend validates via
      // ValidAudience. Access tokens use `aud: "empty"` in Cognito, causing a
      // 401 "The audience 'empty' is invalid" error on every authenticated request.
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        logout,
        confirmEmail,
        resendVerificationCode,
        requestPasswordReset,
        confirmPasswordReset,
        setupMfa,
        verifyMfaSetup,
        enableMfa,
        disableMfa,
        confirmMfaCode,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
